import { ApiErrorMessageInternal, NoddiBadge, NoddiLink, TablePagination } from 'noddi-ui-web';
import { formatPhoneNumber } from 'noddi-util';
import { WorkerAvatar } from '../ServiceWorker/WorkerAvatar';
import type { MemberGroup } from './notificationGroupTypes';
import { acceptsAnyCarSms, acceptsCarSms } from './notificationReach';
import type { NotificationGroupOverviewTranslations } from './notificationTranslations';
import { PickerRow } from './PickerRow';
import { PickerSearch } from './PickerSearch';
import { SectionHeading } from './SectionHeading';
import type { DriverCandidates } from './useDriverCandidates';

type Props = {
  candidates: DriverCandidates;
  /** Who holds the car right now, so the list can say which row the pick takes it from. */
  currentDriverId?: number;
  selectedMember?: MemberGroup;
  onSelect: (member: MemberGroup) => void;
  disabled: boolean;
  /** When set, every row gets a link to that member's detail page (fix a wrong phone or email mid-pick). */
  getMemberDetailUrl?: (member: { userId: number }) => string;
  translations: NotificationGroupOverviewTranslations;
};

/** Step one of the car direction: which member should this car's SMS go to. */
export const CarDriverPicker = ({
  candidates,
  currentDriverId,
  selectedMember,
  onSelect,
  disabled,
  getMemberDetailUrl,
  translations: t
}: Props) => {
  const {
    pagination,
    setPagination,
    search,
    setSearch,
    members: visibleMembers,
    totalCount,
    isFetching,
    error
  } = candidates;

  // Grouped by the pick, not by who drives today: choosing someone swaps the two rows,
  // so the top of the list always answers "who drives this car when I save".
  const picked = selectedMember ? [selectedMember] : [];
  const rest = visibleMembers.filter((member) => member.userGroupMemberId !== selectedMember?.userGroupMemberId);

  const renderMember = (member: MemberGroup) => {
    const isSelected = member.userGroupMemberId === selectedMember?.userGroupMemberId;
    const isOutgoingDriver = !isSelected && member.userGroupMemberId === currentDriverId;
    const phoneNumber = member.phoneNumber ? formatPhoneNumber(member.phoneNumber) : '';

    return (
      // The link sits beside the row, not inside it: PickerRow is a button, and
      // an anchor nested in a button is invalid and steals its click.
      <div key={member.userGroupMemberId} className='flex items-center gap-1'>
        <PickerRow isSelected={isSelected} disabled={disabled} onClick={() => onSelect(member)}>
          <div className='flex min-w-0 flex-col gap-1'>
            <div className='flex min-w-0 items-center gap-3'>
              <WorkerAvatar name={`${member.firstName} ${member.lastName}`} size='sm' />
              <span className='min-w-0 flex-1 truncate text-sm'>{`${member.firstName} ${member.lastName}`}</span>
              {/* Unpicked rows stay one clean line; only the SMS-less exception is worth a badge there. */}
              {!isSelected && !acceptsAnyCarSms(member) && (
                <NoddiBadge variant='warning' className='shrink-0 px-1.5 py-0 text-[10px]'>
                  {t.smsTurnedOff}
                </NoddiBadge>
              )}
              <span className='shrink-0 text-xs text-muted-foreground'>{phoneNumber}</span>
            </div>
            {/* Aligned under the name: what the chosen driver will actually be sent about this car. */}
            {isSelected && (
              <span className='flex flex-wrap gap-1 pl-9'>
                {acceptsCarSms(member, 'booking') && <NoddiBadge variant='outline'>{t.bookingSms}</NoddiBadge>}
                {acceptsCarSms(member, 'service-reminder') && (
                  <NoddiBadge variant='outline'>{t.serviceReminderSms}</NoddiBadge>
                )}
                {!acceptsAnyCarSms(member) && <NoddiBadge variant='warning'>{t.smsTurnedOff}</NoddiBadge>}
              </span>
            )}
            {/* Says which row the pick is taking the car away from, once it has dropped out of the top group. */}
            {isOutgoingDriver && <span className='pl-9 text-xs text-muted-foreground'>{t.currentDriverLabel}</span>}
          </div>
        </PickerRow>
        {getMemberDetailUrl && (
          <NoddiLink
            to={getMemberDetailUrl(member)}
            variant='ghost'
            size='small'
            startIcon='Edit'
            aria-label={`${t.edit} ${member.firstName} ${member.lastName}`.trim()}
            className='shrink-0'
          />
        )}
      </div>
    );
  };

  return (
    <>
      <PickerSearch value={search} onChange={setSearch} placeholder={t.searchMembersPlaceholder} />

      {error && <ApiErrorMessageInternal error={error} />}

      {visibleMembers.length === 0 && <p className='text-sm text-muted-foreground'>{t.noMembersFound}</p>}

      {picked.length > 0 && (
        <div className='flex flex-col gap-2'>
          <SectionHeading icon='User'>{t.driver}</SectionHeading>
          <div className='flex flex-col gap-1'>{picked.map(renderMember)}</div>
        </div>
      )}

      {rest.length > 0 && (
        <div className='flex flex-col gap-2'>
          {picked.length > 0 && <SectionHeading icon='Users'>{t.otherMembers}</SectionHeading>}
          <div className='flex flex-col gap-1'>{rest.map(renderMember)}</div>
        </div>
      )}
      <TablePagination
        pagination={pagination}
        totalCount={totalCount}
        onChange={(next) => setPagination({ ...next, search })}
        loading={isFetching}
        translations={t.pagination}
      />
    </>
  );
};
